/**
 * drawer.js
 *
 * @version 1.0.0
 * @author …
 * @license …
 */

/* ----------------------------------------------------------------------- */
/*  DOM elements                                                           */
/* ----------------------------------------------------------------------- */

const ATTR_TARGET = 'data-drawer-target';
const ATTR_DRAWER = 'data-drawer';
const ATTR_CLOSE = 'data-drawer-close';
const ATTR_CLOSING = 'data-closing';

/* ----------------------------------------------------------------------- */
/*  Event handlers                                                         */
/* ----------------------------------------------------------------------- */

/**
 * Handle all clicks:
 * – open trigger  (`data-drawer-target`)
 * – close button  (`data-drawer-close`)
 * – light-dismiss (`:backdrop`)
 *
 * @param {MouseEvent} event
 */
function handleToggle(event) {
	const trigger = event.target.closest(`[${ATTR_TARGET}]`);

	if (trigger) {
		// Open
		const name = trigger.getAttribute(ATTR_TARGET);
		const selector = `[${ATTR_DRAWER}="${CSS.escape(name)}"]`;
		const dialog = document.querySelector(selector);

		if (dialog) openDialog(dialog);
		return;
	}

	const closeBtn = event.target.closest(`[${ATTR_CLOSE}]`);

	if (closeBtn) {
		// Close
		const dialog = closeBtn.closest('dialog');

		if (dialog) closeDialog(dialog);
		return;
	}

	const dialog = event.target.closest('dialog');

	if (dialog && event.target === dialog) {
		// Light-dismiss
		closeDialog(dialog, 'dismiss');
	}
}

/**
 * Close the first open dialog on Esc.
 * @param {KeyboardEvent} event
 */
function handleEscape(event) {
	if (event.key !== 'Escape') return;

	const openDialog = document.querySelector('dialog[open]');
	if (openDialog) closeDialog(openDialog, 'dismiss');
}

/* ----------------------------------------------------------------------- */
/*  Core helpers                                                           */
/* ----------------------------------------------------------------------- */

/**
 * Open a dialog element with all required setup.
 * @param {HTMLDialogElement} dialog
 */
function openDialog(dialog) {
	// If a polyfill is loaded (Safari/FF <115) ensure it works.
	if (typeof dialog.showModal === 'function') {
		dialog.showModal();
	} else {
		dialog.setAttribute('open', '');
	}

	dialog.removeAttribute('inert');

	/* Cancel event fires when UA would auto-close (Esc, backdrop) */
	dialog.addEventListener('cancel', (e) => {
		e.preventDefault(); // run the animation first
		closeDialog(dialog, 'dismiss');
	}, { once: true });
}

/**
 * Close a dialog after playing a CSS keyframe animation.
 *
 * @param {HTMLDialogElement} dialog
 * @param {string}            [returnValue='']  dialog.close(returnValue)
 */
function closeDialog(dialog, returnValue = '') {
	// Guard: already in its exit phase
	if (dialog.hasAttribute(ATTR_CLOSING)) return;

	dialog.setAttribute(ATTR_CLOSING, '');

	dialog.addEventListener('animationend', () => {
		dialog.removeAttribute(ATTR_CLOSING);

		if (typeof dialog.close === 'function') {
			dialog.close(returnValue);
		} else {
			dialog.removeAttribute('open');
		}

		dialog.setAttribute('inert', '');
	}, { once: true });
}

/**
 * Programmatically open a drawer.
 *
 * Accepts either the drawer element itself *or* the value of
 * its `data-drawer` attribute.
 *
 * @param {HTMLDialogElement|string} target
 */
export function openDrawer(target) {
	let dialog =
		typeof target === 'string'
			? document.querySelector(`[${ATTR_DRAWER}="${CSS.escape(target)}"]`)
			: target;

	if (dialog instanceof HTMLDialogElement) openDialog(dialog);
}

/* ----------------------------------------------------------------------- */
/*  Public API                                                             */
/* ----------------------------------------------------------------------- */

/**
 * Attach dialog behaviour to `document`.
 *
 * @returns {() => void} Cleanup function.
 */
export function initDrawers() {
	document.addEventListener('click', handleToggle, false);
	document.addEventListener('keydown', handleEscape, false);

	return () => {
		document.removeEventListener('click', handleToggle, false);
		document.removeEventListener('keydown', handleEscape, false);
	};
}
